import { useEffect, useState } from "react";
import { TextField, Dialog, DialogActions, DialogContent, DialogTitle, Button, Alert, Grid, Divider, Chip, Autocomplete } from "@mui/material";
import { AdapterMoment } from "@mui/x-date-pickers/AdapterMoment";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import { Countries } from "../../../utils/Lookup";
import { GroupHeader, GroupItems } from "../../../utils/Styled";
import { useAuth } from "../../../hooks/useAuth";
import { GetTokenIdentity } from "../../../utils/Token";

import axios from "axios";
import moment from "moment";
import Swal from "sweetalert2";

export default function CreateTouchpointForm({ open, handleClose, rowData, fetchData }) {
    const { token } = useAuth();
    const identity = GetTokenIdentity(token);

    const [newData, setNewData] = useState({
        org_id: "",
        org_name: "",
        email: "",
        office_phone: "",
        mobile_phone: "",
        pic_name: "",
        product_id: "",
        contact_date: moment().toDate(),
        reminder_date: null,
        is_assigned_to_group: 0,
        assigned_to: "",
        remark: "",
    });
    const [errors, setErrors] = useState({ contact_date: false, assigned_to: false, remark: false });
    const [helperText, setHelperText] = useState({});
    const [errorMessage, setErrorMessage] = useState("");

    const [productsData, setProductsData] = useState([]);
    const [usersData, setUsersData] = useState([]);
    const [selectedProductValue, setSelectedProductValue] = useState(null);
    const [selectedUserValue, setSelectedUserValue] = useState(null);

    useEffect(() => {
        if (open) {
            setNewData({
                org_id: rowData ? rowData.org_id : "",
                org_name: rowData ? rowData.org_name || "" : "",
                email: rowData ? rowData.email || "" : "",
                office_phone: rowData ? rowData.office_phone || "" : "",
                mobile_phone: rowData ? rowData.mobile_phone || "" : "",
                pic_name: rowData ? rowData.pic_name || "" : "",
                product_id: "",
                contact_date: moment().toDate(),
                reminder_date: null,
                is_assigned_to_group: 0,
                assigned_to: "",
                remark: "",
            });
            setSelectedProductValue(null);
            setSelectedUserValue(null);
            setErrors({ contact_date: false, assigned_to: false, remark: false });
            setHelperText({});
            setErrorMessage("");

            axios
                .get(process.env.REACT_APP_WEBSITE_BACKEND_URL + "api/v1/products/select")
                .then((response) => {
                    setProductsData(response.data.data.products);
                })
                .catch((error) => console.log(error));

            axios
                .get(process.env.REACT_APP_WEBSITE_BACKEND_URL + "api/v1/users/groups/select")
                .then((response) => {
                    const users = response.data.data.users;
                    setUsersData(users);

                    const currentUserOption = users.find((option) => Number(option.is_group) === 0 && Number(option.id) === Number(identity.user.user_id));
                    setSelectedUserValue(currentUserOption || null);
                    setNewData((prevData) => ({
                        ...prevData,
                        assigned_to: currentUserOption ? currentUserOption.id : "",
                        is_assigned_to_group: currentUserOption ? 0 : prevData.is_assigned_to_group,
                    }));
                })
                .catch((error) => console.log(error));
        }
    }, [open, rowData, identity.user.user_id]);

    const setError = async (field, message) => {
        await setErrors((prevErrors) => ({ ...prevErrors, [field]: true }));
        await setHelperText((prevHelperText) => ({ ...prevHelperText, [field]: message }));
    };

    const validate = async (field, value) => {
        if (field === "contact_date" && (value === "" || value === null)) {
            await setError("contact_date", "Contact Date is required.");
        } else if (field === "assigned_to" && value === "") {
            await setError("assigned_to", "Assigned To is required.");
        } else if (field === "remark" && (value || "").trim() === "") {
            await setError("remark", "Remark is required.");
        } else {
            await setErrors((prevErrors) => ({ ...prevErrors, [field]: false }));
            await setHelperText((prevHelperText) => ({ ...prevHelperText, [field]: "" }));
        }
    };

    const handleCreate = async () => {
        let isValid = true;

        if (!newData.contact_date) isValid = false;
        if (!newData.assigned_to) isValid = false;
        if (!newData.remark || newData.remark.trim() === "") isValid = false;

        await validate("contact_date", newData.contact_date);
        await validate("assigned_to", newData.assigned_to);
        await validate("remark", newData.remark);

        if (!isValid) {
            setErrorMessage("Please fill all the required fields.");
            return;
        }

        try {
            setErrorMessage("");

            const response = await axios.post(process.env.REACT_APP_WEBSITE_BACKEND_URL + "api/v1/touchpoints/create", {
                org_id: newData.org_id,
                product_id: newData.product_id ? newData.product_id : "",
                contact_date: moment(newData.contact_date).format("YYYY-MM-DD"),
                reminder_date: moment(newData.reminder_date).isValid() ? moment(newData.reminder_date).format("YYYY-MM-DD") : "",
                is_assigned_to_group: newData.is_assigned_to_group,
                assigned_to: newData.assigned_to,
                remark: newData.remark.trim(),
            });

            if (response.status === 201) {
                handleClose();
                Swal.fire({
                    title: "Success",
                    text: "Touchpoint created successfully",
                    icon: "success",
                    showConfirmButton: false,
                    timer: 2000,
                });
                fetchData();
            }
        } catch (error) {
            if (error.response && error.response.data) {
                setErrorMessage(error.response.data.message);
            } else {
                setErrorMessage("An error occurred while creating the touchpoint. Please try again later.");
            }
        }
    };

    return (
        <Dialog
            maxWidth="lg"
            fullWidth
            open={open}
            onClose={(event, reason) => {
                if (reason !== "backdropClick") {
                    handleClose();
                }
            }}
            aria-labelledby="form-dialog-title"
        >
            <DialogTitle id="form-dialog-title">Create New Touchpoint</DialogTitle>
            {errorMessage && (
                <Alert variant="outlined" severity="error" style={{ marginLeft: "24px", marginRight: "24px" }}>
                    {errorMessage}
                </Alert>
            )}
            <DialogContent style={{ paddingBottom: "0px" }}>
                <Divider style={{ paddingTop: "0px", paddingBottom: "4px" }}>
                    <Chip label="Touchpoint Details" />
                </Divider>
                <Grid container spacing={2}>
                    <Grid item xs={12}>
                        <TextField margin="dense" label="Organization" fullWidth value={newData.org_name} InputProps={{ readOnly: true }} sx={{ background: "#e7e7e79e" }} />
                    </Grid>
                </Grid>
                <Grid container spacing={2}>
                    <Grid item xs={3}>
                        <TextField margin="dense" label="Email" fullWidth value={newData.email} InputProps={{ readOnly: true }} sx={{ background: "#e7e7e79e" }} />
                    </Grid>
                    <Grid item xs={3}>
                        <TextField margin="dense" label="Office Phone" fullWidth value={newData.office_phone} InputProps={{ readOnly: true }} sx={{ background: "#e7e7e79e" }} />
                    </Grid>
                    <Grid item xs={3}>
                        <TextField margin="dense" label="Mobile Phone" fullWidth value={newData.mobile_phone} InputProps={{ readOnly: true }} sx={{ background: "#e7e7e79e" }} />
                    </Grid>
                    <Grid item xs={3}>
                        <TextField margin="dense" label="PIC Name" fullWidth value={newData.pic_name} InputProps={{ readOnly: true }} sx={{ background: "#e7e7e79e" }} />
                    </Grid>
                </Grid>
                <Grid container spacing={2}>
                    <Grid item xs={12}>
                        <Autocomplete
                            options={productsData}
                            groupBy={(option) => {
                                const country = Countries.find((country) => country.id === option.country_id);
                                return country ? country.name : "Unknown";
                            }}
                            getOptionLabel={(option) => option.product_description}
                            onChange={(event, value) => {
                                setSelectedProductValue(value);
                                setNewData({ ...newData, product_id: value ? value.product_id : "" });
                            }}
                            isOptionEqualToValue={(option, value) => option.product_id === value.product_id}
                            value={selectedProductValue}
                            renderOption={(props, option) => {
                                return (
                                    <li {...props} key={option.product_id}>
                                        {option.product_description}
                                    </li>
                                );
                            }}
                            renderInput={(params) => <TextField {...params} margin="dense" id="product_id" label="Product" fullWidth />}
                            renderGroup={(params) => {
                                return (
                                    <li key={params.key}>
                                        <GroupHeader>{params.group}</GroupHeader>
                                        <GroupItems>{params.children}</GroupItems>
                                    </li>
                                );
                            }}
                        />
                    </Grid>
                </Grid>
                <Grid container spacing={2}>
                    <Grid item xs={3}>
                        <LocalizationProvider dateAdapter={AdapterMoment}>
                            <DatePicker
                                label="Contact Date"
                                inputFormat="YYYY-MM-DD"
                                value={newData.contact_date}
                                onChange={(newValue) => {
                                    setNewData({ ...newData, contact_date: newValue });
                                    validate("contact_date", newValue);
                                }}
                                renderInput={(params) => <TextField {...params} required fullWidth margin="dense" error={errors.contact_date} helperText={helperText.contact_date} />}
                            />
                        </LocalizationProvider>
                    </Grid>
                    <Grid item xs={3}>
                        <LocalizationProvider dateAdapter={AdapterMoment}>
                            <DatePicker
                                label="Reminder Date"
                                inputFormat="YYYY-MM-DD"
                                value={newData.reminder_date}
                                onChange={(newValue) => {
                                    setNewData({ ...newData, reminder_date: newValue });
                                }}
                                renderInput={(params) => <TextField {...params} fullWidth margin="dense" />}
                            />
                        </LocalizationProvider>
                    </Grid>
                    <Grid item xs={6}>
                        <Autocomplete
                            options={usersData}
                            getOptionLabel={(option) => (option ? option.name : "Select user or group")}
                            groupBy={(option) => option.is_group}
                            onChange={(event, value) => {
                                setSelectedUserValue(value);
                                setNewData({ ...newData, assigned_to: value ? value.id : "", is_assigned_to_group: value ? value.is_group : 0 });
                                validate("assigned_to", value ? value.id : "");
                            }}
                            isOptionEqualToValue={(option, value) => option.id === value.id && option.is_group === value.is_group}
                            value={selectedUserValue}
                            renderInput={(params) => <TextField {...params} margin="dense" id="assigned_to" label="Assigned To" fullWidth required error={errors.assigned_to} helperText={helperText.assigned_to} />}
                            renderGroup={(params) => (
                                <li key={params.key}>
                                    <GroupHeader>{params.group === 1 ? "Groups" : "Users"}</GroupHeader>
                                    <GroupItems>{params.children}</GroupItems>
                                </li>
                            )}
                        />
                    </Grid>
                </Grid>
                {/* <Divider style={{ paddingTop: "16px", paddingBottom: "4px" }}>
                    <Chip label="Additional Details" />
                </Divider> */}
                <TextField
                    autoComplete="off"
                    margin="dense"
                    label="Remark"
                    type="text"
                    multiline
                    rows={4}
                    fullWidth
                    required
                    value={newData.remark}
                    error={errors.remark}
                    helperText={helperText.remark}
                    onChange={(e) => {
                        setNewData({ ...newData, remark: e.target.value });
                        validate("remark", e.target.value);
                    }}
                />
            </DialogContent>
            <DialogActions style={{ margin: "12px" }}>
                <Button onClick={handleCreate} variant="contained" color="primary" style={{ marginRight: "6px", color: "white" }}>
                    Save
                </Button>
                <Button onClick={handleClose} variant="contained" color="error" style={{ color: "white" }}>
                    Cancel
                </Button>
            </DialogActions>
        </Dialog>
    );
}
